import parseHtml from 'html-react-parser'
import { Children, FC, HTMLAttributes, ReactNode } from 'react'

import { cn } from 'src/lib/cn'
import ComponentColor from 'src/types/ItemColor'
import { shapeNumberFn, ShapeNumberOptions, toFixedNumberFn } from 'src/utils/shapeNumber/number'
import componentColorVariants from '../../variants/componentColorVariants'
import ArrowIcon from '../Icons/ArrowIcon'
import ArrowIconDown from '../Icons/ArrowIconDown'

import * as styles from './Number.scss'

const UNIT_MAP = {
  second: 'sec.',
  hour: 'hours',
  minute: 'minutes',
}

type VariationSymbol = {
  positive: ReactNode
  negative: ReactNode
}

export const VariationSymbols: { [key: string]: VariationSymbol } = {
  arrow: {
    positive: <ArrowIcon className={styles.ArrowVariation} />,
    negative: <ArrowIconDown className={styles.ArrowVariation} />,
  },
  plus_minus: { positive: '+', negative: '-' },
  none: { positive: null, negative: null },
}

export interface NumberProps extends HTMLAttributes<HTMLSpanElement> {
  number: number
  shapeNumberOptions?: ShapeNumberOptions
  variationSymbol?: string
  variation?: boolean
  isPositive?: boolean
  equal?: boolean
  badge?: boolean
  neutral?: boolean
  parenthesis?: boolean
  bold?: boolean
  formatCurrency?: boolean
  withVariation?: boolean
  isApproximation?: boolean
  infinity?: boolean
  componentColor?: ComponentColor
  isBadgeHovered?: boolean
  classes?: {
    parenthesis?: string
    currency?: string
    variationSymbol?: string
  }
  unit?: string
}

const defaultShapeNumberOptions = {
  decimals: 2,
  zeroFormat: false,
  displayUnit: true,
  truncate: true,
}

const Number: FC<NumberProps> = ({
  number,
  variation = false,
  variationSymbol = 'plus_minus',
  isPositive: isNumberPositive,
  shapeNumberOptions,
  equal: isNumberEqual,
  badge,
  neutral = false,
  parenthesis = false,
  bold = false,
  className,
  formatCurrency,
  withVariation = false,
  isApproximation,
  infinity,
  componentColor,
  isBadgeHovered,
  color,
  classes,
  unit: unitProp,
  ...othersProps
}) => {
  const options = { ...defaultShapeNumberOptions, ...shapeNumberOptions }
  const fixedNumber = toFixedNumberFn(number, options?.decimals)
  const formattedNumber = shapeNumberFn(Math.abs(fixedNumber), options, formatCurrency)
  const parsedFormattedNumber = Children.toArray(
    parseHtml(formattedNumber, {
      transform: (node: any) => {
        if (node?.props?.className === 'currency') {
          return <span className={cn('currency', classes?.currency)}>{node.props.children}</span>
        }
        return node
      },
    }) as ReactNode,
  )

  const needToShowUnit = unitProp

  const unit = needToShowUnit ? UNIT_MAP[unitProp] : null

  if (!variation) {
    return (
      <span
        data-testid="number-without-variation"
        className={cn(
          styles.Number,
          componentColorVariants({
            text: componentColor !== ComponentColor.blue ? componentColor : null,
          }),
          className,
        )}
        style={{ color }}
        {...othersProps}
      >
        {isApproximation && ' ≈ '}
        {parsedFormattedNumber}
        {unit ? ` ${unit}` : ''}
      </span>
    )
  }

  const equal = isNumberEqual !== undefined ? isNumberEqual : fixedNumber === 0
  const isPositive = isNumberPositive !== undefined ? isNumberPositive : fixedNumber > 0
  const symbol = isPositive
    ? VariationSymbols[variationSymbol].positive
    : VariationSymbols[variationSymbol].negative

  return (
    <span
      data-testid="number-with-variation"
      style={{ color }}
      className={cn(
        {
          [styles.Number]: true,
          [styles.NumberWithVariation]: withVariation,
          [styles.Badge]: badge,
          [styles.Positive]: !equal && !neutral && isPositive,
          [styles.Negative]: !equal && !neutral && !isPositive,
          [styles.Neutral]: equal,
          [styles.PositiveHovered]: isBadgeHovered && !equal && !neutral && isPositive,
          [styles.NegativeHovered]: isBadgeHovered && !equal && !neutral && !isPositive,
          [styles.NeutralHovered]: isBadgeHovered && equal,
          [styles.Bold]: bold,
        },
        'text-nowrap',
        className,
      )}
      {...othersProps}
    >
      {parenthesis ? <span className={classes?.parenthesis}>(</span> : ''}
      {variationSymbol !== 'arrow' && isApproximation && ' ≈ '}
      {!equal ? (
        <span
          className={cn(
            { [styles.ArrowVariationWithMargin]: withVariation },
            classes?.variationSymbol,
          )}
        >
          {symbol}
        </span>
      ) : (
        ''
      )}
      {variationSymbol === 'arrow' && isApproximation && ' ≈ '}
      {!(parenthesis && equal) && ' '}
      {parsedFormattedNumber}
      {unit ? ` ${unit}` : ''}
      {parenthesis ? <span className={classes?.parenthesis}>)</span> : ''}
    </span>
  )
}

export default Number
